// ============================================================================
// CATEGORY HOOK
// ============================================================================
// ./client/src/hooks/useCategory.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import categoryAPI from '../api/categoryAPI';

const useCategory = () => {
  const { 
    categoryState,
    categoryDispatch
  } = useContext(GlobalContext);

  const getAllCategories = useCallback(async (params = {}) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.getAllCategories(params);
      if (response.success) {
        categoryDispatch({ type: 'SET_CATEGORIES', payload: response.data });
        categoryDispatch({ type: 'SET_PAGINATION', payload: response.pagination });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const getCategoryById = useCallback(async (categoryId) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.getCategoryById(categoryId);
      if (response.success) {
        categoryDispatch({ type: 'SET_CURRENT_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const getCategoryByTitle = useCallback(async (title) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.getCategoryByTitle(title);
      if (response.success) {
        categoryDispatch({ type: 'SET_CURRENT_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const createCategory = useCallback(async (data) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.createCategory(data);
      if (response.success) {
        categoryDispatch({ type: 'ADD_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const updateCategory = useCallback(async (categoryId, data) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.updateCategory(categoryId, data);
      if (response.success) {
        categoryDispatch({ type: 'UPDATE_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const deleteCategory = useCallback(async (categoryId, adminData) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.deleteCategory(categoryId, adminData);
      if (response.success) {
        categoryDispatch({ type: 'DELETE_CATEGORY', payload: categoryId });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const addTag = useCallback(async (categoryId, data) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.addTag(categoryId, data);
      if (response.success) {
        categoryDispatch({ type: 'UPDATE_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const removeTag = useCallback(async (categoryId, data) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.removeTag(categoryId, data);
      if (response.success) {
        categoryDispatch({ type: 'UPDATE_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const getCategoriesByTag = useCallback(async (tag) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.getCategoriesByTag(tag);
      if (response.success) {
        categoryDispatch({ type: 'SET_CATEGORIES', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const searchCategories = useCallback(async (query) => {
    categoryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await categoryAPI.searchCategories(query);
      if (response.success) {
        categoryDispatch({ type: 'SET_CATEGORIES', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      categoryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [categoryDispatch]);

  const updateCourseCount = useCallback(async (categoryId) => {
    try {
      const response = await categoryAPI.updateCourseCount(categoryId);
      if (response.success) {
        categoryDispatch({ type: 'UPDATE_CATEGORY', payload: response.data });
      }
      return response;
    } catch (error) {
      categoryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [categoryDispatch]);

  const clearError = useCallback(() => {
    categoryDispatch({ type: 'CLEAR_ERROR' });
  }, [categoryDispatch]);

  const clearCurrentCategory = useCallback(() => {
    categoryDispatch({ type: 'CLEAR_CURRENT_CATEGORY' });
  }, [categoryDispatch]);

  return {
    // categoryState
    categories: categoryState.categories,
    currentCategory: categoryState.currentCategory,
    loading: categoryState.loading,
    error: categoryState.error,
    pagination: categoryState.pagination,
    
    // Actions
    getAllCategories,
    getCategoryById,
    getCategoryByTitle,
    createCategory,
    updateCategory,
    deleteCategory,
    addTag,
    removeTag,
    getCategoriesByTag,
    searchCategories,
    updateCourseCount,
    clearError,
    clearCurrentCategory
  };
};

export default useCategory;